import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import {
  IErrorMessage,
  useErrorMessage
} from '@shared/hooks/use-error-message';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@shared/components/info-tooltip/info-tooltip';
import useMusicStore from '../../../modules/entertainment/pages/playlists/store/playlists.store';
import { PlaylistsDialogFormProps } from '@modules/entertainment/models/playlists.model';
import {
  trimFormValues,
  validateNotOnlySpaces
} from '@shared/utils/trim-form-values-utils';

const PlaylistDialogForm: React.FC<PlaylistsDialogFormProps> = ({
  selectedItem,
  setSelectedItem,
  handleCreateOrUpdate
}) => {
  const {
    register,
    reset,
    getValues,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      name: '',
      url: ''
    }
  });

  const getErrorMessage = useErrorMessage();

  const { t } = useTranslation();
  const { isLoading } = useMusicStore();

  const errorMessages: IErrorMessage = {
    name: {
      required: 'validation_name_required',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    },
    url: {
      required: 'validation_url_required',
      notOnlySpaces: 'validation_notOnly_spaces_required'
    }
  };

  const handleUpdateItem = (name: string, url: string) => {
    const updatedItem = {
      ...selectedItem,
      name: name,
      url: url
    };

    const trimmedData = trimFormValues(updatedItem);

    setSelectedItem(trimmedData);
    return trimmedData;
  };

  const handleSave = async () => {
    const { name, url } = getValues();
    const updatedItem = handleUpdateItem(name, url);
    if (
      url?.toLowerCase().includes('get.php') ||
      url?.toLowerCase().includes('player_api.php')
    ) {
      updatedItem.type = 'xtream';
    } else {
      updatedItem.type = 'm3u8';
    }
    reset();
    await handleCreateOrUpdate(updatedItem);
  };

  useEffect(() => {
    if (selectedItem) {
      reset({
        name: selectedItem?.name || '',
        url: selectedItem?.url || ''
      });
    }
  }, [selectedItem, reset]);

  return (
    <div>
      <form className="flex flex-col gap-1">
        <FormControl error={!!errors.name}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_playlist_name')}
            <InfoTooltip text="tooltip_info_playlist_name" />
          </FormLabel>
          <OutlinedInput
            {...register('name', {
              required: true,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            error={!!errors.name}
            placeholder={t('placeholder_label_playlist_name')}
          />
          <FormHelperText>
            {errors?.name &&
              getErrorMessage({
                errors,
                fieldName: 'name',
                errorMessages
              })}
          </FormHelperText>
        </FormControl>

        <FormControl error={!!errors.url}>
          <FormLabel className="flex">
            <span className="reuqired-symbole">*</span>
            {t('label_playlist_url')}
            <InfoTooltip text="tooltip_info_playlist_url" />
          </FormLabel>
          <OutlinedInput
            {...register('url', {
              required: true,
              validate: {
                notOnlySpaces: validateNotOnlySpaces
              }
            })}
            error={!!errors.url}
            placeholder={t('placeholder_label_playlist_url')}
          />
          <FormHelperText>
            {errors?.url &&
              getErrorMessage({
                errors,
                fieldName: 'url',
                errorMessages
              })}
          </FormHelperText>
        </FormControl>

        <div className="flex justify-end items-center gap-6 mt-4">
          <Button
            disabled={!isValid}
            onClick={handleSave}
            variant="contained"
            loading={isLoading()}
          >
            {t('save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PlaylistDialogForm;
